/**
 * RunningBillsPage.jsx  (iter-326c)
 *
 * Two modes driven by the route param :billId
 *   /running-bills           → list (newest first)
 *   /running-bills/:billId   → detail (Draft = editable; Sent = read-only)
 *
 * Generated from BOQEditPage by hitting
 *   POST /api/boq-worksheets/{worksheet_id}/generate-running-bill
 * Each bill items[].billable_amount = actual_total × task_progress %.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Send, Trash2, Check, IndianRupee, LayoutDashboard, RefreshCw, Lock, AlertTriangle, Search, Filter, ChevronDown, ChevronRight, FolderOpen, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const fmt = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

const StatusBadge = ({ status }) => {
  const map = {
    draft: { txt: 'Draft', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    sent: { txt: 'Sent', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    accepted: { txt: 'Accepted', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
    paid: { txt: 'Paid', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
    cancelled: { txt: 'Cancelled', cls: 'bg-stone-100 text-stone-700 border-stone-300' },
  };
  const m = map[status] || { txt: status || '—', cls: 'bg-stone-100 text-stone-700 border-stone-300' };
  return <Badge variant="outline" className={`text-[10px] font-semibold ${m.cls}`}>{m.txt}</Badge>;
};

const RunningBillsList = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // iter-338d: group by project + collapsible sections.
  const [expandedProjects, setExpandedProjects] = useState({});  // { project_id: bool }
  const [groupByProject, setGroupByProject] = useState(true);
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const loadBills = async () => {
    try {
      const res = await axios.get(`${API}/running-bills`, { headers: authHeaders });
      setBills(res.data || []);
    } catch (e) {
      toast.error('Failed to load running bills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBills();
    // eslint-disable-next-line
  }, [authHeaders]);

  // iter-336: filter + search across bill_number / project / client / BOQ
  const filteredBills = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bills.filter((b) => {
      if (statusFilter !== 'all' && (b.status || 'draft') !== statusFilter) return false;
      if (!q) return true;
      return (
        (b.bill_number || '').toLowerCase().includes(q)
        || (b.project_name || '').toLowerCase().includes(q)
        || (b.client_name || '').toLowerCase().includes(q)
        || (b.boq_worksheet_number || '').toLowerCase().includes(q)
      );
    });
  }, [bills, search, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: bills.length, draft: 0, sent: 0, accepted: 0, paid: 0, cancelled: 0 };
    bills.forEach((b) => { counts[b.status || 'draft'] = (counts[b.status || 'draft'] || 0) + 1; });
    return counts;
  }, [bills]);

  // iter-338d: Group by project for the collapsible UI. Auto-expands the
  // first project so the page never opens fully collapsed.
  const groupedBills = useMemo(() => {
    const groups = new Map();
    filteredBills.forEach((b) => {
      const key = b.project_id || '__no_project__';
      const name = b.project_name || 'Unassigned';
      if (!groups.has(key)) {
        groups.set(key, { project_id: key, project_name: name, bills: [], total: 0 });
      }
      const g = groups.get(key);
      g.bills.push(b);
      g.total += Number(b.grand_total) || 0;
    });
    return Array.from(groups.values()).sort((a, b) => b.bills.length - a.bills.length);
  }, [filteredBills]);

  // Auto-expand the first project on load (or after filter change).
  useEffect(() => {
    if (groupedBills.length > 0 && Object.keys(expandedProjects).length === 0) {
      setExpandedProjects({ [groupedBills[0].project_id]: true });
    }
  }, [groupedBills]); // eslint-disable-line
  const toggleProject = (pid) => setExpandedProjects((s) => ({ ...s, [pid]: !s[pid] }));
  const expandAll = () => setExpandedProjects(Object.fromEntries(groupedBills.map((g) => [g.project_id, true])));
  const collapseAll = () => setExpandedProjects({});

  const handleRowDelete = async (e, bill) => {
    e.stopPropagation();
    if (!window.confirm(`Delete draft running bill ${bill.bill_number}?\n\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/running-bills/${bill.id}`, { headers: authHeaders });
      toast.success(`${bill.bill_number} deleted`);
      setBills((prev) => prev.filter((b) => b.id !== bill.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // iter-338d (Msg-680 follow-up): Archive a sent/accepted bill from the
  // list. Used when the bill is stale or its source BOQ is gone.
  const handleRowArchive = async (e, bill) => {
    e.stopPropagation();
    if (!window.confirm(`Archive ${bill.bill_number}?\n\nThe bill stays in the audit trail but is hidden from this list. Owner/Admin can restore it from Settings → Archived Bills.`)) return;
    try {
      await axios.post(`${API}/running-bills/${bill.id}/archive?reason=manual`, {}, { headers: authHeaders });
      toast.success(`${bill.bill_number} archived`);
      setBills((prev) => prev.filter((b) => b.id !== bill.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Running Bills</h1>
          <p className="text-sm text-stone-500">Generated from BOQ Billable So Far. Newest first.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="rb-list-back" title="Go to Dashboard">
          <LayoutDashboard size={14} className="mr-1" /> Dashboard
        </Button>
      </div>

      {loading && <div className="text-sm text-stone-500">Loading…</div>}
      {!loading && bills.length === 0 && (
        <Card className="p-10 text-center" data-testid="rb-empty-state">
          <FileText className="mx-auto mb-3 text-stone-300" size={42} />
          <div className="text-stone-700 font-medium">No running bills yet.</div>
          <div className="text-sm text-stone-500 mt-1">Open any BOQ, update task progress, then click <span className="font-semibold text-emerald-700">Generate Running Bill</span>.</div>
        </Card>
      )}
      {!loading && bills.length > 0 && (
        <>
          {/* iter-336: Filter chips + search bar */}
          <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="rb-list-toolbar">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by bill #, project, client, or BOQ number…"
                className="pl-9 h-9 bg-white border-stone-200"
                data-testid="rb-search-input"
              />
            </div>
            <div className="flex items-center gap-1.5 text-xs flex-wrap" data-testid="rb-status-filters">
              <Filter size={13} className="text-stone-400 mr-1" />
              {[
                { k: 'all', label: 'All' },
                { k: 'draft', label: 'Draft' },
                { k: 'sent', label: 'Sent' },
                { k: 'accepted', label: 'Accepted' },
                { k: 'paid', label: 'Paid' },
                { k: 'cancelled', label: 'Cancelled' },
              ].map(({ k, label }) => {
                const active = statusFilter === k;
                const count = statusCounts[k] || 0;
                return (
                  <button
                    key={k}
                    onClick={() => setStatusFilter(k)}
                    data-testid={`rb-filter-${k}`}
                    className={`px-3 py-1 rounded-full font-semibold transition-colors border ${active
                      ? 'bg-emerald-600 text-white border-emerald-700 shadow-sm'
                      : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}
                  >
                    {label} <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {filteredBills.length === 0 && (
            <Card className="p-8 text-center" data-testid="rb-no-match-state">
              <Search className="mx-auto mb-3 text-stone-300" size={32} />
              <div className="text-stone-700 font-medium">No bills match your filter.</div>
              <div className="text-sm text-stone-500 mt-1">Try clearing the search or switching to <span className="font-semibold">All</span>.</div>
            </Card>
          )}
          {filteredBills.length > 0 && groupByProject && (
            <div className="space-y-3" data-testid="rb-grouped-list">
              <div className="flex items-center gap-2 text-xs text-stone-500">
                <span>Grouped by project ({groupedBills.length})</span>
                <button onClick={expandAll} className="ml-2 underline text-emerald-700 hover:text-emerald-900" data-testid="rb-expand-all">Expand all</button>
                <span>·</span>
                <button onClick={collapseAll} className="underline text-stone-600 hover:text-stone-900" data-testid="rb-collapse-all">Collapse all</button>
                <span className="ml-auto" />
                <button onClick={() => setGroupByProject(false)} className="underline text-stone-500 hover:text-stone-900" data-testid="rb-toggle-flat">Switch to flat list</button>
              </div>
              {groupedBills.map((g) => {
                const isOpen = !!expandedProjects[g.project_id];
                return (
                  <Card key={g.project_id} className="overflow-hidden" data-testid={`rb-group-${g.project_id}`}>
                    <button
                      onClick={() => toggleProject(g.project_id)}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-stone-50 hover:bg-stone-100 border-b border-stone-200 text-left transition-colors"
                      data-testid={`rb-group-toggle-${g.project_id}`}
                    >
                      {isOpen ? <ChevronDown size={16} className="text-stone-500" /> : <ChevronRight size={16} className="text-stone-500" />}
                      <FolderOpen size={14} className="text-emerald-600" />
                      <span className="font-semibold text-stone-800">{g.project_name}</span>
                      <span className="text-xs text-stone-500">· {g.bills.length} bill{g.bills.length === 1 ? '' : 's'}</span>
                      <span className="ml-auto text-xs font-semibold text-stone-700">{fmt(g.total)}</span>
                    </button>
                    {isOpen && (
                      <table className="w-full text-sm">
                        <thead className="bg-white border-b border-stone-100">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold text-stone-500 uppercase text-[10px]">Bill #</th>
                            <th className="px-4 py-2 text-left font-semibold text-stone-500 uppercase text-[10px]">BOQ</th>
                            <th className="px-4 py-2 text-right font-semibold text-stone-500 uppercase text-[10px]">Progress</th>
                            <th className="px-4 py-2 text-right font-semibold text-stone-500 uppercase text-[10px]">Grand Total</th>
                            <th className="px-4 py-2 text-center font-semibold text-stone-500 uppercase text-[10px]">Status</th>
                            <th className="px-4 py-2 text-left font-semibold text-stone-500 uppercase text-[10px]">Created</th>
                            <th className="px-4 py-2 text-center font-semibold text-stone-500 uppercase text-[10px]">Open</th>
                            <th className="px-4 py-2 text-center font-semibold text-stone-500 uppercase text-[10px]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.bills.map((b) => (
                            <tr key={b.id} className="border-b border-stone-100 hover:bg-emerald-50/30 cursor-pointer" onClick={() => navigate(`/running-bills/${b.id}`)} data-testid={`rb-row-${b.bill_number}`}>
                              <td className="px-4 py-3 font-bold text-emerald-700 underline">{b.bill_number}</td>
                              <td className="px-4 py-3 text-stone-500 text-xs">{b.boq_worksheet_number || '-'}</td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">{b.simple_progress_pct ?? 0}%</span>
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-stone-900">{fmt(b.grand_total)}</td>
                              <td className="px-4 py-3 text-center"><StatusBadge status={b.status} /></td>
                              <td className="px-4 py-3 text-stone-500 text-xs">{b.created_at ? new Date(b.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                              <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                <Link to={`/running-bills/${b.id}`} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`rb-open-${b.bill_number}`}>
                                  Open <ArrowLeft className="rotate-180" size={11} />
                                </Link>
                              </td>
                              <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                {b.status === 'draft' ? (
                                  <button onClick={(e) => handleRowDelete(e, b)} className="inline-flex items-center justify-center w-8 h-8 rounded-md text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors" data-testid={`rb-row-delete-${b.bill_number}`} title={`Delete draft ${b.bill_number}`}>
                                    <Trash2 size={14} />
                                  </button>
                                ) : (
                                  <button onClick={(e) => handleRowArchive(e, b)} className="inline-flex items-center justify-center w-8 h-8 rounded-md text-amber-600 hover:bg-amber-50 border border-transparent hover:border-amber-200 transition-colors" data-testid={`rb-row-archive-${b.bill_number}`} title={`Archive ${b.bill_number} (cannot delete a ${b.status} bill — archived instead).`}>
                                    <Archive size={14} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
          {filteredBills.length > 0 && !groupByProject && (
        <Card className="overflow-hidden">
          <div className="px-4 py-2 bg-stone-50 border-b text-xs text-stone-500 flex items-center">
            <span>Flat list ({filteredBills.length} bills)</span>
            <button onClick={() => setGroupByProject(true)} className="ml-auto underline text-emerald-700 hover:text-emerald-900" data-testid="rb-toggle-grouped">Group by project</button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-stone-600 uppercase text-xs">Bill #</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600 uppercase text-xs">Project</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600 uppercase text-xs">BOQ</th>
                <th className="px-4 py-3 text-right font-semibold text-stone-600 uppercase text-xs">Progress</th>
                <th className="px-4 py-3 text-right font-semibold text-stone-600 uppercase text-xs">Grand Total</th>
                <th className="px-4 py-3 text-center font-semibold text-stone-600 uppercase text-xs">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-stone-600 uppercase text-xs">Created</th>
                <th className="px-4 py-3 text-center font-semibold text-stone-600 uppercase text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBills.map((b) => (
                <tr key={b.id} className="border-b border-stone-100 hover:bg-emerald-50/30 cursor-pointer" onClick={() => navigate(`/running-bills/${b.id}`)} data-testid={`rb-row-${b.bill_number}`}>
                  <td className="px-4 py-3 font-bold text-emerald-700 underline">{b.bill_number}</td>
                  <td className="px-4 py-3">{b.project_name || '-'}</td>
                  <td className="px-4 py-3 text-stone-500 text-xs">{b.boq_worksheet_number || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      {b.simple_progress_pct ?? 0}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-stone-900">{fmt(b.grand_total)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={b.status} /></td>
                  <td className="px-4 py-3 text-stone-500 text-xs">{b.created_at ? new Date(b.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {b.status === 'draft' ? (
                      <button
                        onClick={(e) => handleRowDelete(e, b)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
                        data-testid={`rb-row-delete-${b.bill_number}`}
                        title={`Delete draft ${b.bill_number}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => handleRowArchive(e, b)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-amber-600 hover:bg-amber-50 border border-transparent hover:border-amber-200 transition-colors"
                        data-testid={`rb-row-archive-${b.bill_number}`}
                        title={`Archive ${b.bill_number}`}
                      >
                        <Archive size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
          )}
        </>
      )}
    </div>
  );
};

const RunningBillDetail = ({ billId }) => {
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const load = async () => {
    const res = await axios.get(`${API}/running-bills/${billId}`, { headers: authHeaders });
    setBill(res.data);
    setNotes(res.data?.notes || '');
    setItems(res.data?.items || []);
  };

  useEffect(() => { load(); }, [billId]);  // eslint-disable-line

  const isDraft = bill?.status === 'draft';

  const updateItemBillable = (idx, val) => {
    const next = items.map((it, i) => i === idx ? { ...it, billable_amount: Number(val) || 0 } : it);
    setItems(next);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/running-bills/${billId}`, { notes, items }, { headers: authHeaders });
      toast.success('Saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm(`Send ${bill.bill_number} to ${bill.client_name || 'client'}? Once sent, the bill is locked.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/running-bills/${billId}/send`, {}, { headers: authHeaders });
      toast.success(`${bill.bill_number} marked as Sent`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete draft bill ${bill.bill_number}?`)) return;
    try {
      await axios.delete(`${API}/running-bills/${billId}`, { headers: authHeaders });
      toast.success('Deleted');
      navigate('/running-bills');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // iter-332: Re-sync this draft bill against the latest BOQ state
  const handleResync = async (forceMode = false) => {
    const baseMsg = `Re-sync ${bill.bill_number} from the latest BOQ?\n\nThis will:\n  • Refresh actual qty / rate / progress from the BOQ\n  • Add any NEW BOQ items\n  • Tag items removed from BOQ with ⚠\n  • Preserve any manual billable_amount overrides (🔒 badge)\n\nNotes, bill number and date are kept.`;
    const forceMsg = `\u26a0\ufe0f Force Re-sync ${bill.bill_number} (status: ${bill.status})?\n\nThis bill has already been sent / accepted. Forcing a re-sync will OVERWRITE the issued figures with the latest BOQ rates / quantities / progress.\n\nUse this only when the original bill was issued with stale BOQ data (e.g. a rate was corrected AFTER the bill went out).\n\nAn audit-log entry will record this action.`;
    if (!window.confirm(forceMode ? forceMsg : baseMsg)) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/running-bills/${billId}/resync${forceMode ? '?force=true' : ''}`,
        {},
        { headers: authHeaders }
      );
      const s = r.data?.summary || {};
      toast.success(
        r.data?.message ||
        `Synced · +${s.added || 0} new · −${s.removed || 0} removed · ${s.overrides_preserved || 0} override(s) preserved`,
        { duration: 6000 }
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-sync failed');
    } finally {
      setBusy(false);
    }
  };

  // iter-338 (Msg-680): Bulk-delete every row currently tagged as "Removed from BOQ".
  // Available only on draft bills. Mirrors the "Apply all suggestions" UX the user
  // approved as a P2 enhancement.
  const handleBulkRemoveRemoved = async () => {
    const removedCount = (items || []).filter(it => it.removed_from_boq && it.item_type !== 'header').length;
    if (removedCount === 0) {
      toast.info('No "Removed from BOQ" rows to clean up.');
      return;
    }
    if (!window.confirm(`Remove ${removedCount} item(s) tagged "Removed from BOQ" from this draft bill?\n\nThis is irreversible for the bill — the items still exist in the audit trail of the source BOQ.`)) return;
    setBusy(true);
    try {
      const cleaned = (items || []).filter(it => !(it.removed_from_boq && it.item_type !== 'header'));
      await axios.patch(`${API}/running-bills/${billId}`, { items: cleaned }, { headers: authHeaders });
      toast.success(`${removedCount} removed row(s) cleaned from the bill.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk remove failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRestoreBoq = async () => {
    const msg = `Restore a BOQ from ${bill.bill_number}?\n\nThis creates a NEW Draft BOQ in the project with the same items as this bill. Useful when the source BOQ was deleted but you still need it back (e.g. to keep working on it).\n\nThe bill is automatically re-pointed to the new BOQ so future Re-sync calls work.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/running-bills/${billId}/restore-boq`, {}, { headers: authHeaders });
      const newId = r.data?.boq_worksheet_id;
      toast.success(`Restored — ${r.data?.items_count || 0} item(s). Opening new BOQ…`, { duration: 4000 });
      if (newId) navigate(`/boq-worksheets/${newId}/edit`);
      else await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  if (!bill) return <div className="p-10 text-stone-500">Loading…</div>;

  const billableTotal = items.reduce((s, it) => s + (Number(it.billable_amount) || 0), 0);

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="rb-detail-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="rb-dashboard-btn" title="Go to Dashboard">
            <LayoutDashboard size={14} className="mr-1" /> Dashboard
          </Button>
          {bill.boq_worksheet_id && bill.archive_reason !== 'boq_deleted' && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/boq-worksheets/${bill.boq_worksheet_id}/edit`)} data-testid="rb-back-to-boq-btn" title="Open the source BOQ">
              <FileText size={14} className="mr-1" /> Back to BOQ
            </Button>
          )}
          {/* iter-338d (Msg-680 round-3): "Restore BOQ from this bill" — shown
             when the source BOQ is missing (orphaned bill). One click creates
             a fresh Draft BOQ from the bill's item snapshot. Owner/Admin only. */}
          {bill.archive_reason === 'boq_deleted' && !bill.restored_boq_at && (
            <Button variant="outline" size="sm" onClick={handleRestoreBoq} disabled={busy} className="text-emerald-700 border-emerald-300 bg-emerald-50/40 hover:bg-emerald-100" data-testid="rb-restore-boq-btn" title="Re-create a Draft BOQ from this bill's items (since the source BOQ was deleted).">
              <FileText size={14} className="mr-1" /> Restore BOQ
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate('/running-bills')} data-testid="rb-all-bills-btn">
            <ArrowLeft size={14} className="mr-1" /> All Bills
          </Button>
          <h1 className="text-2xl font-bold text-stone-900">{bill.bill_number}</h1>
          <StatusBadge status={bill.status} />
          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            {bill.simple_progress_pct ?? 0}% complete
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isDraft && (
            <>
              <Button size="sm" variant="outline" onClick={() => handleResync(false)} disabled={busy} className="text-violet-700 border-violet-300 bg-violet-50/40 hover:bg-violet-100" data-testid="rb-resync-btn" title="Refresh from the latest BOQ — adds new items, marks removed ones, preserves manual overrides.">
                <RefreshCw size={14} className="mr-1" /> Re-sync from BOQ
              </Button>
              {(items || []).some(it => it.removed_from_boq && it.item_type !== 'header') && (
                <Button size="sm" variant="outline" onClick={handleBulkRemoveRemoved} disabled={busy} className="text-rose-700 border-rose-300 bg-rose-50/40 hover:bg-rose-100" data-testid="rb-bulk-remove-removed-btn" title="Bulk-remove all rows tagged 'Removed from BOQ' (cleanup helper)">
                  <Trash2 size={14} className="mr-1" /> Apply all suggestions
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={handleDelete} className="text-red-600 border-red-200" data-testid="rb-delete-btn">
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
              <Button size="sm" variant="outline" onClick={handleSave} disabled={busy} data-testid="rb-save-btn">
                <Check size={14} className="mr-1" /> {busy ? 'Saving…' : 'Save Draft'}
              </Button>
              <Button size="sm" onClick={handleSend} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rb-send-btn">
                <Send size={14} className="mr-1" /> Send to Client
              </Button>
            </>
          )}
          {/* iter-338 (Msg-680): Force Re-sync from BOQ for sent/accepted bills.
             Admin-gated server-side; UI shows the button regardless and the
             API returns 403 for non-admin users. */}
          {!isDraft && bill.boq_worksheet_id && (
            <Button size="sm" variant="outline" onClick={() => handleResync(true)} disabled={busy} className="text-amber-800 border-amber-300 bg-amber-50/40 hover:bg-amber-100" data-testid="rb-force-resync-btn" title="Force re-sync from BOQ — overwrites issued figures with the latest BOQ rates / qty / progress. Audit-logged.">
              <RefreshCw size={14} className="mr-1" /> Force Re-sync from BOQ
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><div className="text-xs text-stone-500 uppercase">Project</div><div className="font-semibold">{bill.project_name}</div></div>
          <div><div className="text-xs text-stone-500 uppercase">Client</div><div className="font-semibold">{bill.client_name || '-'}</div></div>
          <div><div className="text-xs text-stone-500 uppercase">Source BOQ</div><div className="font-semibold"><Link to={`/boq-worksheets/${bill.boq_worksheet_id}/edit`} className="text-blue-600 underline">{bill.boq_worksheet_number}</Link></div></div>
          <div><div className="text-xs text-stone-500 uppercase">Created</div><div className="font-semibold">{bill.created_at ? new Date(bill.created_at).toLocaleString('en-IN') : '-'}</div></div>
        </div>
        {bill.last_resync_at && (
          <div className="mt-3 pt-3 border-t border-stone-100 text-[11px] text-violet-700 flex items-center gap-1.5" data-testid="rb-last-resync-info">
            <RefreshCw size={11} className="text-violet-500" />
            <span className="font-semibold">Last re-synced from BOQ</span> on
            {' '}{new Date(bill.last_resync_at).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
            {bill.last_resync_by_name && <> by <span className="font-semibold">{bill.last_resync_by_name}</span></>}
          </div>
        )}
      </Card>

      <Card className="overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-xs uppercase">#</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Item</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Actual Qty</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Actual Amt</th>
              <th className="px-3 py-2 text-center text-xs uppercase">Progress</th>
              <th className="px-3 py-2 text-right text-xs uppercase bg-emerald-50 border-l-2 border-emerald-300">Billable So Far</th>
              {isDraft && <th className="px-3 py-2 text-center text-xs uppercase">—</th>}
            </tr>
          </thead>
          <tbody>
            {(() => {
              let itemNum = 0;
              return items.map((it, idx) => {
                const isHeader = it.item_type === 'header' || it.is_header;
                if (isHeader) {
                  return (
                    <tr key={it.item_id || idx} className="bg-amber-50 border-y border-amber-200" data-testid={`rb-header-row-${idx}`}>
                      <td colSpan={isDraft ? 7 : 6} className="px-3 py-2 font-bold text-amber-900 text-xs uppercase tracking-wide">
                        {it.task_title || it.description || 'Section'}
                      </td>
                    </tr>
                  );
                }
                itemNum += 1;
                const isOverride = it.manual_override === true;
                const isRemoved = it.removed_from_boq === true;
                return (
              <tr key={it.item_id || idx} className={`border-b border-stone-100 ${isRemoved ? 'bg-red-50/40' : ''}`} data-testid={`rb-item-row-${idx}`}>
                <td className="px-3 py-2 text-stone-500">{itemNum}</td>
                <td className="px-3 py-2">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    <span className={isRemoved ? 'line-through text-stone-500' : ''}>{it.task_title}</span>
                    {isRemoved && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300" data-testid={`rb-removed-badge-${idx}`} title="This item was removed from the BOQ after the bill was generated. It is kept here for audit; delete the row manually if you don't want to bill for it.">
                        <AlertTriangle size={10} />Removed from BOQ
                      </span>
                    )}
                    {isOverride && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300" data-testid={`rb-override-badge-${idx}`} title="Billable amount was manually overridden — re-sync left it untouched.">
                        <Lock size={10} />Manually overridden
                      </span>
                    )}
                  </div>
                  {it.description && <div className="text-[11px] text-stone-500 line-clamp-2">{it.description.replace(/<[^>]*>/g, '').slice(0, 120)}</div>}
                </td>
                <td className="px-3 py-2 text-right">{it.actual_quantity}</td>
                <td className="px-3 py-2 text-right">{fmt(it.actual_total)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[11px] font-semibold border px-2 py-0.5 rounded-full ${(it.task_progress || 0) > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-stone-400 bg-stone-50 border-stone-200'}`}>{it.task_progress || 0}%</span>
                </td>
                <td className="px-3 py-2 text-right bg-emerald-50/60 border-l-2 border-emerald-300">
                  {isDraft ? (
                    <Input
                      type="number"
                      value={it.billable_amount}
                      onChange={(e) => updateItemBillable(idx, e.target.value)}
                      className="w-28 ml-auto text-right font-bold text-emerald-800 h-8 bg-white"
                      data-testid={`rb-billable-input-${idx}`}
                    />
                  ) : (
                    <span className={`font-bold ${(Number(it.billable_amount) || 0) > 0 ? 'text-emerald-800' : 'text-stone-300'}`}>{fmt(it.billable_amount)}</span>
                  )}
                </td>
                {isDraft && (
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700" data-testid={`rb-remove-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                )}
              </tr>
                );
              });
            })()}
          </tbody>
          <tfoot className="bg-stone-50 border-t-2 border-stone-300">
            <tr className="bg-emerald-100">
              <td colSpan={5} className="px-3 py-2 text-right font-bold text-emerald-900">Basic Total (Billable So Far):</td>
              <td className="px-3 py-2 text-right font-bold text-emerald-900 bg-emerald-200" data-testid="rb-basic-total">{fmt(billableTotal)}</td>
              {isDraft && <td></td>}
            </tr>
            {Number(bill.discount_amount) > 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-2 text-right">Discount:</td>
                <td className="px-3 py-2 text-right text-red-600">-{fmt(bill.discount_amount)}</td>
                {isDraft && <td></td>}
              </tr>
            )}
            {Number(bill.overhead_freight) > 0 && (
              <tr><td colSpan={5} className="px-3 py-2 text-right">Freight:</td><td className="px-3 py-2 text-right">{fmt(bill.overhead_freight)}</td>{isDraft && <td></td>}</tr>
            )}
            {Number(bill.overhead_labour) > 0 && (
              <tr><td colSpan={5} className="px-3 py-2 text-right">Labour Handling:</td><td className="px-3 py-2 text-right">{fmt(bill.overhead_labour)}</td>{isDraft && <td></td>}</tr>
            )}
            {Number(bill.overhead_consultancy) > 0 && (
              <tr><td colSpan={5} className="px-3 py-2 text-right">Consultancy:</td><td className="px-3 py-2 text-right">{fmt(bill.overhead_consultancy)}</td>{isDraft && <td></td>}</tr>
            )}
            {Number(bill.gst_amount) > 0 && (
              <tr><td colSpan={5} className="px-3 py-2 text-right">GST:</td><td className="px-3 py-2 text-right">{fmt(bill.gst_amount)}</td>{isDraft && <td></td>}</tr>
            )}
            <tr className="bg-stone-900 text-white">
              <td colSpan={5} className="px-3 py-3 text-right font-bold">
                <span className="inline-flex items-center gap-1"><IndianRupee size={14} /> Grand Total:</span>
              </td>
              <td className="px-3 py-3 text-right font-bold text-amber-300" data-testid="rb-grand-total">{fmt(bill.grand_total)}</td>
              {isDraft && <td></td>}
            </tr>
          </tfoot>
        </table>
      </Card>

      <Card className="p-4">
        <div className="text-xs uppercase text-stone-500 mb-2 font-semibold">Notes / Memo</div>
        {isDraft ? (
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. RA-2 covering work up to 15-Feb. Payment terms: 7 days."
            className="min-h-[80px]"
            data-testid="rb-notes-input"
          />
        ) : (
          <div className="text-sm text-stone-700 whitespace-pre-wrap">{bill.notes || <span className="text-stone-400 italic">No notes added.</span>}</div>
        )}
      </Card>
    </div>
  );
};

export default function RunningBillsPage() {
  const { billId } = useParams();
  return billId ? <RunningBillDetail billId={billId} /> : <RunningBillsList />;
}
